/* eslint-disable max-len */
import {
  ArchivedState, CasePhase, CaseState, Company, ComplianceCheckList, CountryCode, CreateCustomTemplateInput, EmploymentBasedCategory, FileType, ForeignNational, PassportGender, ProcessRequestLocationType, Report, ReportEntries, Role, TemplateCategory, TemplateType, VisaStatus, CompanyAddressType, VisaAdditionalProcessing,
  AOSFilingEligibility,
} from 'API';
import { FileCategory } from 'models';
import { Link } from '@mui/material';
import {
  ALL_ROLES, MH_EMPLOYER_ROLES, MH_FN, MH_ROLES,
} from 'services/auth';
import {
  AccessPermission,
  CapType, EditPermission, Entity, TimeUnit, ExecMode, reportingFieldTypes, resolvers, InviteStatus, inviteStatusResolver, CompanyContactLabel, contactLabelResolver, selectionStatusResolver, casePreparationResolver,
} from 'shared/enums';
import { countryCodeResolver } from './geoResolvers';

const fileTypeResolver = resolvers.FileType;
const complianceCheckListResolver = resolvers.ComplianceCheckListResolver;
const visaAdditionalProcessingResolver = resolvers.VisaAdditionalProcessing;

export const FEATURES_PATH = 'src/__tests__/features/';

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday'];

export const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const AMPLIFY_PASSWORD_CHALLENGE = 'NEW_PASSWORD_REQUIRED';
export const SHOW_MODAL = 'SHOW_MODAL';

export const FORM_MAX_LENGTH = 60;

export const MONEY_MAX_LENGTH = 15;

export const RECEIPT = {
  SELECTION_NOTICE_NO: 'SelectionNoticeNo',
};

export const CASE = 'case';
export const JOB_URL = 'jobURL';

export const ALREADY_DELETED_FILETYPE = 'ALREADY_DELETED_FILETYPE';

export const DICTIONARY = {
  AdditionalDocuments: 'Additional Documents',
  AddJobDescription: 'Add Job Description',
  AddNewContact: 'Add new contact',
  AddNewIndividualsInformation: 'Add new individual‘s information.',
  Address: 'Street Number and Name',
  AllCases: 'All cases',
  AllCasesByClient: 'All cases by client',
  AllClients: 'All clients',
  Approve: 'Approve',
  Approved: 'Approved',
  InReview: 'In Review',
  Archive: 'Archive',
  Associations: 'Associations',
  AuthError: 'Either the username or password was incorrect. Please try again.',
  ApproachingExpiration: 'Approaching expiration',
  ApproachingExpirationShort: 'Approaching exp.',
  ArchivePrompt: 'Archive this profile?',
  AutocompletePrompt: 'Start typing to search',
  AutocompleteSearching: 'Searching …',
  BirthCountryAbbreviation: 'C.O.B.',
  BlogHead: 'Recent Developments',
  CancelReset: 'Back to log in',
  CareOf: 'In care of',
  CasesAssignedTo: 'Cases assigned to',
  CasesDueThisMonth: 'Cases due this month',
  ChangeImage: 'Change image',
  City: 'City',
  ClientTeam: 'Your client team',
  Closed: 'Closed',
  Company: 'Company',
  CompanyContacts: 'Company Contacts',
  CompanyDocuments: 'Company Documents',
  CompanyProfile: 'Company Profile',
  CompanyJobs: 'Company Jobs',
  ContactNumber: 'Contact number',
  Country: 'Country',
  Create: 'Create',
  CreateAdmin: 'Add an Administrator',
  CreateCompany: 'Add New Company',
  NewDocumentCreated: 'New document type added successfully.',
  DocumentCategoryUpdated: 'Document category updated successfully.',
  CreateCompanyTooltip: 'New Company',
  CreateEmployer: 'Add a Client',
  CreateForeignNational: 'Add a Foreign National',
  CreateNewCaseButton: 'Create New Case',
  CreateNewCaseTitle: 'Initiate New Case',
  CreateNewCaseTooltip: 'New Case',
  CreateNewProcessRequestTooltip: 'New Process Request',
  CreateNewReport: 'New Report',
  CreateReportTitle: 'Create a New Report',
  CreateNewUserButton: 'Add',
  CreateNewUserRolePrompt: 'What type of profile are you adding?',
  CreateNewUserTitle: 'Create New Profile',
  CreateNewUserTooltip: 'New User',
  CreateTeamMember: 'Add a Team Member',
  PersonalEmail: 'Personal Email',
  PersonalPhone: 'Personal Phone Number',
  CriminalHistory: {
    tortureGenocide: '   Acts involving torture or genocide?',
    killing: 'Killing any person?',
    severeInjuring: 'Intentionally and severely injuring an person?',
    nonconsensualSex: 'Engaging in an kind of sexual contact or relations with an person who did not consent or was unable to consent, or was being forced or threatened?',
    religiousBelief: 'Limiting or denying any person’s ability to exercise religious beliefs?',
  },
  ChooseTemplate: 'Choose Template',
  DefaultError: 'Something went wrong.',
  DefaultFamilyContact: 'Family Contact',
  DomainWhitelist: 'Associated domains',
  DownloadError: 'There was an error downloading this file.',
  DueThisMonth: 'Due this month',
  DueToday: 'Due today',
  DeleteReportQuestion: 'Are you sure you want to delete this report?',
  DeleteTemplateQuestion: 'Are you sure you want to delete this template along with ALL associated reports?',
  DeleteReportWarning: 'Caution: Deleting this report is permanent and cannot be undone.',
  DeleteTemplateWarning: 'Caution: This is a permanent action and cannot be undone.',
  HardDeleteTemplateSuccess: 'Template and associated reports will be deleted.',
  SoftDeleteTemplateSuccess: 'Template will be deleted.',
  Edit: 'Save',
  Complete: 'Complete',
  EditCompany: 'Edit Company',
  EditMilestone: 'Edit Milestone',
  CompleteMilestone: 'Complete Milestone',
  EditJobDescription: 'Edit Job Description',
  EditProfile: 'Edit profile',
  EditTemplate: 'Edit Template',
  EditTemplateDetails: 'Edit Template Details',
  Email: 'Email',
  EmailAddress: 'Email address',
  EmailRequiredForLogin: `This person must have an email address in order to 
      allow them to log in.`,
  EmailTaken: 'This email address is already used by someone in the system.',
  EmailVerificationCodeSent: `We sent a verification code to the new email
      address. You will be asked for it the next time you log in.`,
  EmptyContacts: 'None found.',
  EnableLoginPrompt: 'Enable user to log in?',
  EndDate: 'End date',
  EndDateChange: 'Change end date',
  ExistingEmployee: 'Existing Employee',
  ExistingIndividual: 'Existing Individual',
  Explain: 'Explain',
  ErrorSendingReport: 'Something went wrong while sending report.',
  ErrorDeletingTemplate: 'Something went wrong while deleting template.',
  ErrorValidatingReceiptNumber: 'Invalid format. Use valid receipt number(s)',
  ErrorValidatingCompanyWhitelistedDomains: 'Invalid format.',
  FamilyContacts: 'Family Contacts',
  FedExAccount: 'Fed Ex account',
  Firstname: 'First name',
  Firm: 'Meltzer Hellrung',
  FailedReport: 'This report is failed.',
  ForeignNational: 'Foreign National',
  ForeignNationalLower: 'Foreign national',
  ForeignNationals: 'Foreign Nationals',
  FormFieldError: 'There was a problem with at least one of the fields.',
  FnIndividual: 'Only Show Individual FNs',
  FrequencyTypeError: 'Frequency must be a number',
  CaseIndividual: 'Only Non-company Sponsored Cases',
  HideAdvancedOptions: 'Hide Advanced Options',
  I140: {
    classificationPrompt:
      'What Green Card Category was it approved under?',
    priorityDatePrompt: 'What is your priority date?',
    petitionerI140: 'I-140 Petitioner',
  },
  ImmigrationInformation: 'Immigration Information',
  IndicateDependents: 'Indicate Dependents',
  InviteButtonText: 'Invite',
  InviteUserTitle: 'Profile Invitation',
  IncludeFnFamilyContacts: 'Include Family Contacts',
  IncludeArchievedUsers: 'Include Archieved Users',
  IncludeCaseFamiyContacts: 'Include Family Contacts\' Cases',
  JobDescriptions: 'Job Descriptions',
  JobDescription: {
    address: 'Work Address',
    addressIsClientSite: 'Is this a client site?',
    canWorkFromHome: 'Is working from home an option?',
    certificationLicenses: 'Certification/Licenses',
    degrees: 'Degrees Required',
    dotNumber: 'DOT Code',
    caseSocCode: 'SOC Code',
    duties: 'Job Duties',
    majors: 'Majors Required',
    objectiveSkillSetsRequired: 'Objective Skill Sets Required',
    officialTitle: 'Official Forms Title',
    offeredSalary: 'Salary Range',
    special: 'Requirements for Position',
    supervisesOthers: 'Will this position supervise others?',
    supervisesOthersCount: 'How many people?',
    supervisesTitles: 'What are their titles?',
    supervisorTitle: 'Supervisor’s Title',
    title: 'Job Title',
    travelIsRequired: 'Is travel required?',
    travelPercentage: 'What percentage of travel time is required?',
    travelType: 'What kind of travel is required?',
    yearsGeneralExperience: 'Years of General Experience Required',
  },
  Subscribe: 'Subscribe',
  JobInformation: 'Job Description',
  KnowledgeCenter: 'Knowledge Center',
  MostRecentInvite: 'Most recent invitation',
  InvalidEmail: 'This does not appear to be a valid email address.',
  JobDetails: 'Job Details',
  JobTitleTaken: 'A job description already exists with that title.',
  Lastname: 'Last name',
  LegalNameOfUSCompany: 'Legal Name of US Company',
  TypeOfProcess: 'Type of Process',
  VisaInformation: 'Visa Information',
  DestinationCountry: 'Destination Country',
  TypeOfVisa: 'Type of Visa',
  CoCounselInformation: 'Co-Counsel Information',
  AddNewVendor: 'Add New Vendor',
  AddNewCoCounsel: 'Add New Co-Counsel',
  VendorCreateFailed: 'We could not save this vendor. Please try again.',
  VendorSearchFailed: `We could not search vendors right now. Please try
      again before adding a new one.`,
  Location: 'Location',
  LinkedCompanies: 'Linked Employer Companies',
  LinksHead: 'Quick Shortcuts',
  Middlename: 'Middle name',
  NewPassword: 'New password',
  NewPasswordHeadline: 'Set new password',
  NonEmployerError: 'Primary contacts must be employers. This email is already in the system but belongs to a non-employer.',
  NoOptions: 'No options found',
  NoRelatives:
    'This foreign national has not been linked to any other family members.',
  NoRelativesSelf: 'You have not been linked to any other family members.',
  Open: 'Open',
  OnlyVersionReport: 'This is the only version of the report.',
  OneOffReportFilters: 'Filters are not editable here. Please create a new report to adjust filters.',
  Password: 'Password',
  PersonNotFound: 'This person was not found.',
  Phone: 'Phone Number',
  PostalCode: 'Postal Code',
  ProcessType: 'Process Type',
  Profile: 'Profile',
  PreferredMailingAddress: 'Preferred mailing address',
  PrimaryContact: 'Primary contact',
  ProcessRequests: 'Process Requests',
  PreviewModalHeader: 'This is a preview of column headers only. Data will be displayed after the report has been run.',
  PendingReport: 'This report is pending.',
  PendingScheduledReport: 'The scheduled report for this template has not been created yet.',
  PendingDeleteReport: 'The report for this template has not been created yet.',
  QuickRunReport: 'Quick Run Report',
  ReenableUserButton: 'Reactivate',
  ReenableUserTitle: 'Reactivate User',
  RequestDocument: 'Request document',
  RequestDocumentShort: 'Request doc',
  RequestVerificationCode: 'Request new code',
  RequiredField: 'This is a required field.',
  RequiresExpiration: 'Files of this type require an expiration date.',
  DurationOfStatus: 'Exp. Date: D/S',
  RequiresIssueDate: 'Files of this type require an issue date.',
  Resend: 'Resend',
  ResetCode: 'Reset code',
  ResetError: 'There was an error with your request.',
  ResetPassword: 'Reset password',
  ResetPasswordHeadline: 'Request password reset',
  ResetCodePrompt: `A verification code has been sent to your email address.
    Enter it here along with a new password.`,
  Resubmit: 'Resubmit',
  Revoke: 'Revoke',
  ReportAccess: 'Give Report Access',
  ReportSentSuccessfully: 'Report has been sent successfully.',
  SaveError: 'An error occured while saving.',
  ConnectionUpdateError: 'The company was saved, but some attorney, paralegal or subsidiary changes could not be applied. Please reopen the company and check them.',
  RunReport: 'Run Report',
  RequiredFieldError: 'This field is required.',
  ReportViewAccessHeading: 'Companies and Client Users with Access',
  SaveAsTemplate: 'Save as a New Template',
  SchedulerTitle: 'Schedule Template Automation',
  ShowModal: 'ShowModal',
  SearchProfile: 'Search to find an archived profile.',
  SearchFields: 'Search to Add Filters',
  SelectCompanyFirst: 'Select a company first.',
  SelectIndividual: 'Select Individual',
  SelectReport: 'Select Report',
  SelectReportType: 'Select Report Type',
  SelectIndividuals: 'Share with Individual Users',
  SelectClients: 'Search and Select Client Users',
  Search: 'Type to search...',
  SendCode: 'Send code',
  SendMessage: 'Send message',
  SetNewPassword: 'Set new password',
  ShareForm: 'Share form',
  ShareReport: 'Share Report',
  ShareReportHelperText: 'Sharing reports only provides users with access - it does not send notifications to those users. To notify a user, select "Send Report" from the report menu after sharing the report with them.',
  ShareReportCompaniesHelperText: 'Reports shared with companies can be accessed by all associated employer users.',
  ShowAdvancedOptions: 'Show Advanced Options',
  SidebarFilterLabels: {
    all: 'All active',
    archived: 'Archived',
    assignedUser: 'Assigned to me',
    attorneys: 'Attorneys',
    clients: 'Clients',
    contributors: 'Contributors',
    independent: 'Non-company sponsored',
    nationals: 'Foreign nationals',
    paralegals: 'Paralegals',
    parentCompanies: 'Parent companies',
    pending: 'Pending',
    sponsored: 'Company sponsored',
    seniorContacts: 'Senior contacts',
    signatoryContacts: 'Signatory contacts',
    primaryContacts: 'Primary contacts',
    invoicingContacts: 'Invoicing contacts',
  },
  SignIn: 'Sign in',
  SignInHeadline: 'Sign in to your account',
  SpecialNotes: 'Special Notes',
  SponsorCompany: 'Sponsor Company',
  SponsorshipType: 'Sponsorship Type',
  StartDate: 'Start date',
  StartReportWith: 'Select Report Creation Method',
  SelectCompanies: 'Select Companies',
  SelectEmployers: 'Select Employers',
  SelectTeamMembers: 'Select Team Members',
  StringFilterHelperText: 'This is a case sensitive filter.',
  CasePersonFilterHelperText: 'Type at least 3 characters to search.',
  DueDate: 'Due date',
  CompletionDate: 'Completion date',
  StartDateChange: 'Change start date',
  State: 'State',
  StateProvince: 'State or province',
  SubmittedRequests: 'Submitted Requests',
  TabClients: 'Clients',
  TabColleagues: 'Colleagues',
  TabCompanies: 'Companies',
  TabCompanyContacts: 'Company Contacts',
  TabFamily: 'Family Members',
  TabForeignNationals: 'Foreign Nationals',
  TabLegalTeam: 'Legal Team',
  TabYou: 'You',
  TeamMembers: 'Team members',
  TemplateViewAccess: 'Who should have access to view and run this report template? *',
  TemplateEditAccess: 'Who should have edit permission for this report template? *',
  ThisMonth: 'This month',
  ThisYear: 'This year',
  Title: 'Title',
  TotalCases: 'Total cases',
  Unarchive: 'Unarchive',
  UnitNumber: 'Unit Number',
  UnitType: 'Unit Type',
  UploadError: 'There was an error uploading this file.',
  UploadImage: 'Upload image',
  UPSAccount: 'UPS Account',
  UseCommas: 'Use commas to separate values.',
  UserAgreeementAccept: 'I accept',
  VerificationCode: 'Verification code',
  Verify: 'Verify',
  VerifyCodeSent: 'Code sent',
  VerifyEmailPrompt: `For security purposes, we need to verify your email.
    Please enter the code that was just sent to your email address.`,
  ViewAll: 'View all',
  ViewDetails: 'View details',
  ViewMore: 'View more',
  viewAccessError: 'View Access is required.',
  viewAccessCompanyError: 'Select atleast one company to grant access',
  Visas: 'Visas',
  WhitelistMismatchError: `This email address is not on the list of this
    company’s associated domains.`,
  WhitelistInstructions: `Which domains are allowed for employers? Use commas
    to separate domains.`,
  WhitelistInstructionsFirm: `Which domains are allowed for employees of this 
    firm? Use commas to separate domains.`,
  WithTemplateReportFilters: 'Filters are not editable here. Please edit the template to adjust filters for future reports from this template.',
  YouParanthetical: '(you)',
  YourCases: 'Your cases',
  YourPriorityCases: 'Your priority cases',
  YourTasks: 'Your tasks',
  YourTeam: 'Your team',
  Zip: 'ZIP Code',
  ZipRequest: 'Your files are being compiled. We\'ll email you a download link when they\'re ready.',
  ZipRequestFailed: 'We couldn\'t start compiling your files. Please try again.',
  CompanyNotes: 'Company Notes',
  PetitionerParagraph: 'Petitioner\'s Paragraph',
  ForeignNationalNotes: 'Foreign National Notes',
  ErrorMessageOfUserExistInCognito: 'An account with the given email already exists. Please Resend the invite',
  SelectRecipients: 'Select Recipients',
  EMCantViewMHCreatedTemplateCompany: 'This field can only be edited by your legal team.',
  CaseStatusSave: "You've removed the following receipt number(s), which will also remove their status details. Do you want to proceed?",
  ReceiptNumberFilterHelperText: 'Enter a receipt number or multiple receipt numbers separated by commas.',
  CompanyWhitelistedDomainsFilterHelperText: 'Enter a domain or multiple domains separated by commas.',
  CaseStatus: 'Case Status',
  CaseStatusDescription: 'Case Status Description',
  ReceiptNumber: 'Select a receipt number',
  DocumentTypeRequired: 'What type of document is required?',
  CreateCustomFileRequirement: 'Create Custom File Requirement',
  AddDocumentType: 'Add Document Type',
  SubscribeToNewsletter: 'The Meltzer Hellrung newsletter provides regular updates on policy and other immigration-related topics. Get immigration news, product updates, and exclusive insights sent straight to your inbox.',
};

export const ALWAYS_FILE_TYPES: Array<{
  type: FileType;
  category: FileCategory;
  instructions?: React.ReactNode
  defaultStorage: 'company' | 'national' | 'processRequest' | 'case';
  visibility?: string[]
}> = [
  {
    type: FileType.PASSPORT,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: 'Upload a scan of your most recent passport, including the ID page.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.OTHER,
    category: FileCategory.OTHER,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.I_797_APPROVAL_NOTICE,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload most recent I-797 Approval Notice. You will only have an I-797 if you\'ve filed a prior application with the USCIS. If you\'ve only applied for a visa at a U.S. consulate, you will not have an I-797.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },

];

// “Generate RFGP” (VOYAG-801) in the Build RFGP modal. Turned off for now:
// selecting and ordering still work. Set to true to enable generation again.
export const RFGP_GENERATE_ENABLED = true;

// Document types the system creates files for (flatten / RFGP). They are never
// added to case.fileRequirements: CaseDocuments shows their card to firm staff
// whenever matching files exist, and users can't upload into them.
export const SYSTEM_FILE_TYPES: FileType[] = [
  FileType.FLATTENED_FORMS,
  FileType.RFGP_FINAL_SUBMISSION,
];

// Visibility is to handle custom visibility for a specific file
export const FILE_TYPES: Array<{
  type: FileType;
  category: FileCategory;
  instructions?: React.ReactNode;
  defaultStorage: 'company' | 'national' | 'processRequest' | 'case';
  visibility?: string[];
  expirationDateRequired?: boolean;
  issueDateRequired?: boolean;
}> = [
  {
    type: FileType.I_140_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.DS_160_COPY,
    category: FileCategory.APPLICATIONS_APPROVALS,
    visibility: MH_FN,
    defaultStorage: 'national',
  },
  {
    type: FileType.DOL_AUDIT_RESPONSE,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.E_3_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.E_3D_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    visibility: ALL_ROLES,
    defaultStorage: 'national',
  },
  {
    type: FileType.E_3D_CONSULAR_PACKET,
    category: FileCategory.APPLICATIONS_APPROVALS,
    visibility: ALL_ROLES,
    defaultStorage: 'national',
  },
  {
    type: FileType.H_1B_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.H_1B1_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.H_4_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.H_4_CONSULAR_PACKET,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PERM_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.TD_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.TD_BORDER_PACKET,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.TD_CONSULAR_PACKET,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.TN_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.APPLICATION_COPY_EMPLOYEE,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.COMPANY_FINANCIAL_STATEMENT,
    category: FileCategory.COMPANY,
    instructions: 'Upload most recent financial statement(s), such as Tax Return, Audited Financial Statement and/or Annual Report.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.MARKETING_MATERIAL,
    category: FileCategory.COMPANY,
    instructions: 'Upload marketing materials, such as screenshots of website.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.DIPLOMA,
    category: FileCategory.EDUCATION,
    instructions: 'Upload all documents with translations (if applicable).',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.EDUCATION_EVALUATION,
    category: FileCategory.EDUCATION,
    instructions: 'Upload all educational evaluation documents.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SCHOOL_MARK_TRANSCRIPT,
    category: FileCategory.EDUCATION,
    instructions: 'Upload all school marks and transcripts.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.JOB_DESCRIPTION,
    category: FileCategory.EMPLOYMENT,
    defaultStorage: 'processRequest',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.VENDOR_CLIENT_LETTERS,
    category: FileCategory.EMPLOYMENT,
    instructions: 'If there are mid-vendors involved between your company and the ultimate end-client, please provide any additional agreements that evidence the subcontracting relationship.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.MASTER_AGREEMENT,
    category: FileCategory.EMPLOYMENT,
    instructions: `If the employee will be primarily located at an end-client worksite, 
    please provide the Master Service Agreement and/or Statement of Work that encompasses the employee's project.`,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.W2,
    category: FileCategory.EMPLOYMENT,
    instructions: 'Upload most recent W-2.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PAY_STUBS,
    category: FileCategory.EMPLOYMENT,
    instructions: 'Only provide your two most recent paycheck stubs if you are currently in the U.S. working for a U.S. employer.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SIGNED_EMPLOYMENT_CONTRACT,
    category: FileCategory.EMPLOYMENT,
    instructions: 'Make sure the employment offer and/or employment contract is signed by both the foreign national and a representative from the company.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.EXPERIENCE_LETTER,
    category: FileCategory.FORMS_LETTERS,
    instructions: 'Upload experience letters from past employers.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.COMPLETED_FORMS,
    category: FileCategory.FORMS_LETTERS,
    defaultStorage: 'case',
    visibility: MH_ROLES,
  },
  {
    type: FileType.FLATTENED_FORMS,
    category: FileCategory.FORMS_LETTERS,
    instructions: 'Flat PDF copies created with “Convert to flat PDF”. The originals are unchanged.',
    defaultStorage: 'case',
    visibility: MH_ROLES,
  },
  {
    type: FileType.RFGP_FINAL_SUBMISSION,
    category: FileCategory.FORMS_LETTERS,
    instructions: 'The merged final submission PDF generated with “Build RFGP”.',
    defaultStorage: 'case',
    visibility: MH_ROLES,
  },
  {
    type: FileType.USCIS_PRIMARY_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.EVIDENCE_OF_PRIMARY_USCIS,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload copy of primary USCIS Visa holder\'s current passport ID page, I-94 record, I-797 Approval Notice, and two recent paystubs',
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.USCIS_DEPENDENT_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SUPPORT_LETTER,
    category: FileCategory.FORMS_LETTERS,
    defaultStorage: 'national',
    visibility: MH_ROLES,
  },
  {
    type: FileType.PREMIUM_PROCESSING_UPGRADE_FORMS,
    category: FileCategory.FORMS_LETTERS,
    defaultStorage: 'national',
    visibility: MH_ROLES,
  },
  {
    type: FileType.I_140_APPROVAL_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.I_140_RECEIPT_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.APPROVAL_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.CERTIFIED_PERM,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.DOL_AUDIT_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.OTHER_NOTICES,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.RECEIPT_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.REQUEST_FOR_EVIDENCE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.OTHER,
    category: FileCategory.OTHER,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.BIRTH_CERTIFICATE,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: 'Upload a scan of your birth certificate, including English translations if in a foreign language.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.MARRIAGE_CERTIFICATE,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: 'Only required for spouses.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PASSPORT,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: 'Upload a scan of your most recent passport, including the ID page.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PASSPORT_PHOTO,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: (
      <>
        Upload passport-style photo.
        {' '}
        <Link href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener">Reference can be found here</Link>
        .
      </>
    ),
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.RESUME,
    category: FileCategory.PERSONAL_IDENTIFICATION,
    instructions: 'Upload current resume.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.ACTUAL_PREVAILING_WAGE_MEMO,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.CERTIFIED_LABOR_CONDITION_APPLICATION,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.LABOR_CONDITION_APPLICATION_NOTICE_FILLING,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.OES_WAGES,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.OTHER,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.PAF_TEMPLATES,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SIGNED_CERTIFIED_LCA,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SIGNED_NOTICE_FILING_CONFIRMATION,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.SUMMARY_OF_BENEFITS,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.DEPENDENT_VISA_USCIS_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions: 'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.EMPLOYMENT_VISA_CONSULAR_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions: 'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.EMPLOYMENT_VISA_USCIS_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions: 'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.PERM_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions: 'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.EAD_CARD,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload current EAD card. If you have not applied for work authorization on Form I-765, you will not have this document.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.EVIDENCE_OF_PRIMARY_E3,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload copy of primary E-3 holder\'s current passport ID page, I-94 record, I-797 Approval Notice, and 2 recent paystubs',
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.EVIDENCE_OF_PRIMARY_H1B,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload copy of primary H-1B holder\'s current passport ID page, I-94 record, I-797 Approval Notice, and 2 recent paystubs',
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.EVIDENCE_OF_PRIMARY_TN,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload copy of primary TN holder\'s current passport ID page, I-94 record, I-797 Approval Notice, and two recent paystubs',
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.I_20_COPY,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'If you attended school in the U.S., upload all I-20s.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.I_797_APPROVAL_NOTICE,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload most recent I-797 Approval Notice. You will only have an I-797 if you\'ve filed a prior application with the USCIS. If you\'ve only applied for a visa at a U.S. consulate, you will not have an I-797.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.I_94,
    category: FileCategory.US_IMMIGRATION,
    instructions: (
      <>
        Upload all I-94 records for spouses and children here. You can retrieve your most recent I-94 record by visiting
        {' '}
        <Link href="https://i94.cbp.dhs.gov/I94/#/home" target="_blank" rel="noopener">this page</Link>
        . You must type in the information from the passport you last used to enter the U.S. to retrieve your I-94 record.
      </>
    ),
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PREVIOUS_I_140_APPROVAL_NOTICES,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload all previous I-140 Approval Notices.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.PREVIOUS_I_797_APPROVAL_NOTICES,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload all previous I-797 Approval Notices. You will only have an I-797 if you\'ve extended your status with the USCIS. If you\'ve only applied for a visa extension at a U.S. consulate, you will not have an I-797. ',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.REQUEST_EVIDENCE_RESPONSE,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,

  },
  {
    type: FileType.PRIOR_EAD,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload all past EAD cards. If you have not applied for work authorization on Form I-765, you will not have this document. ',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.VISA_STAMPS,
    category: FileCategory.US_IMMIGRATION,
    instructions: 'Upload scans of all visa stamps and all stamped passport pages.',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  // Extra Documents
  {
    type: FileType.H_1B_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions:
          'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
  },
  {
    type: FileType.H_4_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions:
          'Please do not date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
  },
  {
    type: FileType.H_1B_RECEIPT_NOTICE,
    category: FileCategory.US_IMMIGRATION,
    defaultStorage: 'national',
  },
  {
    type: FileType.H_4_RECEIPT_NOTICE,
    category: FileCategory.US_IMMIGRATION,
    defaultStorage: 'national',
  },
  {
    type: FileType.PRIOR_I_20,
    category: FileCategory.US_IMMIGRATION,
    defaultStorage: 'national',
  },
  {
    type: FileType.H_1B1_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    instructions: 'Please do no date the signature pages. Dates will be added in future use.',
    defaultStorage: 'company',
  },
  {
    type: FileType.H_1B1_APPROVAL_NOTICE,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
  },
  {
    type: FileType.H_1B1_RECEIPT_NOTICE,
    category: FileCategory.US_IMMIGRATION,
    defaultStorage: 'national',
  },
  {
    type: FileType.BIRTH_CERTIFICATE_OR_MARRIAGE_CERTIFICATE,
    category: FileCategory.EMPLOYMENT,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.EAD_COPIES,
    category: FileCategory.US_IMMIGRATION,
    defaultStorage: 'national',
  },
  {
    type: FileType.STATEMENT_OF_WORK,
    category: FileCategory.EMPLOYMENT,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.OTHER_PUBLIC_ACCESS_FILE,
    category: FileCategory.PUBLIC_ACCESS_FILE,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  // EAD FILE TYPES
  {
    type: FileType.EAD_SIGNATURE_PAGES,
    category: FileCategory.SIGNATURE_PAGES,
    defaultStorage: 'national',
    visibility: MH_FN,
  },
  {
    type: FileType.EAD_APPLICATION,
    category: FileCategory.APPLICATIONS_APPROVALS,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },

  {
    type: FileType.H1B_LOTTERY_SELECTION_NOTICE,
    category: FileCategory.GOVERNMENT_NOTICES,
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.GREEN_CARD,
    category: FileCategory.US_IMMIGRATION,
    instructions: '',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
    expirationDateRequired: true,
  },
  // PERM Recruitment File Types
  {
    type: FileType.CANDIDATE_RESUMES_AND_ASSESSMENTS,
    category: FileCategory.PERM_RECRUITMENT,
    instructions: 'Upload all candidate resumes and assessments.',
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.EMPLOYEE_REFERRAL_PROGRAM,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.SUNDAY_NEWSPAPER_AD_1,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.SUNDAY_NEWSPAPER_AD_2,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.STATE_WORKFORCE_AGENCY_POSTING,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.WEEKDAY_ETHNIC_NEWSPAPER_AD,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.RADIO_AD,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.CAMPUS_PLACEMENT,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.JOB_FAIR,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.COMPANY_WEBSITE,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.JOB_SEARCH_ENGINE,
    category: FileCategory.PERM_RECRUITMENT,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.ATTORNEY_CLIENT_AGREEMENT,
    category: FileCategory.COMPANY,
    defaultStorage: 'company',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.PREVAILING_WAGE_DETERMINATION,
    category: FileCategory.GOVERNMENT_NOTICES,
    instructions: '',
    defaultStorage: 'national',
    visibility: MH_EMPLOYER_ROLES,
  },
  {
    type: FileType.ADVANCED_PAROLE,
    category: FileCategory.US_IMMIGRATION,
    instructions: '',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.RE_ENTRY_PERMIT,
    category: FileCategory.US_IMMIGRATION,
    instructions: '',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
  {
    type: FileType.DS_2019,
    category: FileCategory.US_IMMIGRATION,
    instructions: '',
    defaultStorage: 'national',
    visibility: ALL_ROLES,
  },
];

export const ALL_FILE_TYPES = FILE_TYPES
  .map((type) => type.type)
  .sort(
    (a: FileType, b: FileType) => {
      const textA = fileTypeResolver(a);
      const textB = fileTypeResolver(b);

      return textA > textB ? 1 : -1;
    },
  );

// File types that should be options when uploading files to a Process
// request and where the file should be saved — company or national —
// in addition to the process request.
export const PROCESS_REQUEST_FILE_TYPES = [
  FileType.BIRTH_CERTIFICATE,
  FileType.COMPANY_FINANCIAL_STATEMENT,
  FileType.I_140_APPROVAL_NOTICE,
  FileType.PREVIOUS_I_140_APPROVAL_NOTICES,
  FileType.PREVIOUS_I_797_APPROVAL_NOTICES,
  FileType.DIPLOMA,
  FileType.EAD_CARD,
  FileType.JOB_DESCRIPTION,
  FileType.EDUCATION_EVALUATION,
  FileType.EXPERIENCE_LETTER,
  FileType.I_20_COPY,
  FileType.I_797_APPROVAL_NOTICE,
  FileType.I_94,
  FileType.MARKETING_MATERIAL,
  FileType.MARRIAGE_CERTIFICATE,
  FileType.MASTER_AGREEMENT,
  FileType.PASSPORT,
  FileType.PASSPORT_PHOTO,
  FileType.PAY_STUBS,
  FileType.PRIOR_EAD,
  FileType.RESUME,
  FileType.SCHOOL_MARK_TRANSCRIPT,
  FileType.SIGNED_EMPLOYMENT_CONTRACT,
  FileType.VENDOR_CLIENT_LETTERS,
  FileType.VISA_STAMPS,
  FileType.W2,
  FileType.CANDIDATE_RESUMES_AND_ASSESSMENTS,
  FileType.GREEN_CARD,
  // PERM Recruitment
  FileType.SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF,
  FileType.EMPLOYEE_REFERRAL_PROGRAM,
  FileType.SUNDAY_NEWSPAPER_AD_1,
  FileType.SUNDAY_NEWSPAPER_AD_2,
  FileType.STATE_WORKFORCE_AGENCY_POSTING,
  FileType.WEEKDAY_ETHNIC_NEWSPAPER_AD,
  FileType.RADIO_AD,
  FileType.CAMPUS_PLACEMENT,
  FileType.JOB_FAIR,
  FileType.COMPANY_WEBSITE,
  FileType.JOB_SEARCH_ENGINE,
  // Extra Documents
  FileType.H_1B_RECEIPT_NOTICE,
  FileType.H_4_RECEIPT_NOTICE,
  FileType.ATTORNEY_CLIENT_AGREEMENT,
].sort((a: FileType, b: FileType) => {
  const textA = fileTypeResolver(a);
  const textB = fileTypeResolver(b);

  return textA > textB ? 1 : -1;
});

export const LOGOS = {
  grey: '/img/logos/mh-logo--circle-grey.svg',
  primary: '/img/logos/mh-logo--circle.svg',
  translucent: '/img/logos/mh-logo--circle-translucent.svg',
  white: '/img/logos/mh-logo--circle-white.svg',
  voyager: '/img/logos/voyager.svg',
  voyagerWide: '/img/logos/voyager-wide.svg',
  voyagerMH: '/img/logos/voyager-mh--800.png',
};

export const PLATFORM_NAME = 'Voyager';

export const PUBLIC_ICONS = {
  wave: '/img/wave.png',
};

// Ensure these are in sync with options in createSlug.js
export const SLUGIFY_OPTIONS = {
  strict: true,
  lower: true,
  locale: 'en',
};

export const ALLOWED_FILE_TYPES = 'image/png,image/jpeg,image/tiff,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

export const MAX_FILE_SIZE = {
  bytes: 1000 * 1000 * 1024,
  text: '1 gigabyte',
};

export const HOME_TZ = 'America/Chicago';
export const CASE_CAP_OPTION = ['Bachelor’s Degree', 'U.S. Master’s Degree or Higher'];
export const PAGE_SIZE = 10;
export const REPORTING_PAGE_SIZE = 50;
export const DATE_FORMAT = 'M/d/yy';
export const ACCEPT_FOR_IMAGES = {
  'file/png': ['.png'],
  'file/jpg': ['.jpg'],
  'file/jpeg': ['.jpeg'],
  'file/tiff': ['.tiff'],
  'file/pdf': ['.pdf'],
  'file/doc': ['.doc'],
  'file/docx': ['.docx'],
  'file/xls': ['.xls'],
  'file/xlsx': ['.xlsx'],
  'file/ppt': ['.ppt'],
  'file/pptx': ['.pptx'],
};
export const Report_Table_Columns_Width = [
  { id: '1', width: '11%' },
  { id: '2', width: '7.3%' },
  { id: '3', width: '9.2%' },
  { id: '4', width: '9.2%' },
  { id: '5', width: '9.2%' },
  { id: '6', width: '9.2%' },
  { id: '7', width: '9.2%' },
  { id: '8', width: '9.2%' },
  { id: '9', width: '9.2%' },
  { id: '10', width: '9.2%' },
  { id: '11', width: '7.3%' },
];

const I_485J_FILING = 'I-485J Filing';

export const keyDateActionResolver = (action: string) => {
  switch (action) {
    case I_485J_FILING:
      return 'I-485 Filing';
    default:
      return action;
  }
};

export const OPEN_CASES = 'Open Case';

export const EmptyDashboardHeading1 = 'You are all caught up!';

export const EmptyDashboardHeading2 = 'There are no new cases for you to review at this time.';

export const CREATE_NEW_REPORT = 'Create Report';

// Requesting these documents, the assigned sponsor should be notified
export const DocumentsAssignedToSponor = [
  'COMPANY_FINANCIAL_STATEMENT',
  'MARKETING_MATERIAL',
  'MASTER_AGREEMENT',
  'VENDOR_CLIENT_LETTERS',
  'CANDIDATE_RESUMES_AND_ASSESSMENTS',
  'EMPLOYEE_REFERRAL_PROGRAM',
  'SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF',
  'SUNDAY_NEWSPAPER_AD_1',
  'SUNDAY_NEWSPAPER_AD_2',
  'STATE_WORKFORCE_AGENCY_POSTING',
  'WEEKDAY_ETHNIC_NEWSPAPER_AD',
  'RADIO_AD',
  'CAMPUS_PLACEMENT',
  'JOB_FAIR',
  'COMPANY_WEBSITE',
  'JOB_SEARCH_ENGINE',
];

export const ReceiptNoticesWithLiveCaseStatus = [
  'I485JReceiptNotice',
  'USCISReceiptNumber',
  'AOSReceiptNotice',
];

export interface Field {
  fieldName: string;
  label: string;
  fieldType: string;
  isActive?: boolean;
  filter?: string;
  orderNo?: number;
}

export interface CustomTemplateFields {
  fieldName: string;
  label: string;
  isActive: boolean;
  category: TemplateCategory[]
  entity: Entity,
  filter: string;
  orderNo: number;
  fieldType?: string;
  filterType?: string | null;
  value?: string | string[] | null;
  dateRange?: Array<string>;
  startDate?: string;
  endDate?: string;
  exactDate?: string;
  operator?: string | null;
  subOperator?: string | null;
  customNumber?: number | null;
  fileType?: string;
}

export type EnumType = ArchivedState | CountryCode | EmploymentBasedCategory | CasePhase | CapType | ComplianceCheckList | VisaStatus;

export interface FilterEnumType {
  fieldName: EnumType;
  enum: EnumType;
  resolver?: (code:EnumType) => string;
}

export const EnumReportFilters = [
  {
    fieldName: 'archived',
    enum: ArchivedState,
  },
  {
    fieldName: 'citizenshipCountry',
    enum: CountryCode,
    resolver: (code: CountryCode) => countryCodeResolver(code),
  },
  {
    fieldName: 'chargeabilityCountry',
    enum: CountryCode,
    resolver: (code: CountryCode) => countryCodeResolver(code),
  },
  {
    fieldName: 'employmentBasedCategory',
    enum: EmploymentBasedCategory,
    resolver: (code: EmploymentBasedCategory) => resolvers.EmploymentBasedCategory(code),
  },
  {
    fieldName: 'currentPhase',
    enum: CasePhase,
    resolver: (code: CasePhase) => resolvers.CasePhase(code),
  },
  {
    fieldName: 'capType',
    enum: CapType,
  },
  {
    fieldName: 'capGap',
    enum: ComplianceCheckList,
    resolver: (code: ComplianceCheckList) => complianceCheckListResolver(code),
  },
  {
    fieldName: 'visaAdditionalProcessing',
    enum: VisaAdditionalProcessing,
    resolver: (code: VisaAdditionalProcessing) => visaAdditionalProcessingResolver(code),
  },
  {
    fieldName: 'aosFilingEligible',
    enum: AOSFilingEligibility,
    resolver: (code: AOSFilingEligibility) => resolvers.AOSFilingEligibility(code),
  },
  {
    fieldName: 'stemExtEligible',
    enum: ComplianceCheckList,
    resolver: (code: ComplianceCheckList) => complianceCheckListResolver(code),
  },
  {
    fieldName: 'educationEvaluationNeeded',
    enum: ComplianceCheckList,
    resolver: (code: ComplianceCheckList) => complianceCheckListResolver(code),
  },
  {
    fieldName: 'wageIssues',
    enum: ComplianceCheckList,
    resolver: (code: ComplianceCheckList) => complianceCheckListResolver(code),
  },
  {
    fieldName: 'caseNature',
    enum: {
      Lottery_Submission_Only: 'lottery-submission-only',
      Full_Preparation: 'full-preparation',
      To_Be_Determined: 'to-be-determined',
    },
    resolver: (code: string) => casePreparationResolver(code),
  },
  {
    fieldName: 'selection',
    enum: {
      Selected: 'selected',
      Not_Selected: 'not-selected',
      Awaiting_Selection_Notice: 'awaiting-selection-notice',
    },
    resolver: (code: string) => selectionStatusResolver(code),
  },
  {
    fieldName: 'passportGender',
    enum: PassportGender,
    resolver: (code: PassportGender) => resolvers.PassportGender(code),
  },
  {
    fieldName: 'preferredMailingAddress',
    enum: CompanyAddressType,
    resolver: (preferredAddressType: CompanyAddressType) => resolvers.CompanyPreferredMailingAddress(preferredAddressType),
  },
  {
    fieldName: 'birthCountry',
    enum: CountryCode,
    resolver: (code: CountryCode) => countryCodeResolver(code),
  },
  {
    fieldName: 'lastPhaseCompleted',
    enum: CasePhase,
    resolver: (code: CasePhase) => resolvers.CasePhase(code),
  },
  {
    fieldName: 'immigrationCurrentStatus',
    enum: VisaStatus,
    resolver: (code: VisaStatus) => resolvers.VisaStatus(code),
  },
  {
    fieldName: 'state',
    enum: CaseState,
    resolver: (code: CaseState) => resolvers.CaseState(code),
  },
  {
    fieldName: 'requestedProcess',
    enum: VisaStatus,
    resolver: (code: VisaStatus) => resolvers.VisaStatus(code),
  },
  {
    fieldName: 'inviteStatus',
    enum: InviteStatus,
    resolver: (code: InviteStatus) => inviteStatusResolver(code),
  },
  {
    fieldName: 'companyContactLabel',
    enum: CompanyContactLabel,
    resolver: (code: CompanyContactLabel) => contactLabelResolver(code),
  },
  {
    fieldName: 'currentProcessType',
    enum: VisaStatus,
    resolver: (code: VisaStatus) => resolvers.VisaStatus(code),
  },
  {
    fieldName: 'lastProcessType',
    enum: VisaStatus,
    resolver: (code: VisaStatus) => resolvers.VisaStatus(code),
  },
  {
    fieldName: 'workLocation',
    enum: ProcessRequestLocationType,
    resolver: (code: ProcessRequestLocationType) => resolvers.ProcessRequestLocationType(code),
  },
];

export interface TemplatePayload {
  payload: CreateCustomTemplateInput;
  schedulers: Scheduler[];
  companyIds: Array<string> | null;
  numberOfColumns: number;
  numberOfFilters: number;
  runReport?: boolean;
  execMode: ExecMode;
  templateID?: string;
  fromTemplate: boolean;
  reRunReport?: {
    ranBy: string;
    actorType: string;
  };
  parentReportName?: string;
}
export interface GenerateReportPayload extends TemplatePayload {
  reportName: string;
  createdBy: String | null | undefined;
  runReport: boolean;
  execMode: ExecMode;
}

export type UpdateTemplatePayload = {
  templateID: string;
  reRunReport?: {
    ranBy: string;
    actorType: string;
  };
  updatedByRole: Role
  updatedBy: string
} & Omit<TemplatePayload, 'templateID'>;

export type UpdateTemplateInput = Pick<UpdateTemplatePayload, 'templateID'> & GenerateReportPayload & {};

export const defaultSelectedFields = [
  {
    fieldName: 'firstname',
    label: 'First Name',
    fieldType: 'string',
  },
  {
    fieldName: 'middlename',
    label: 'Middle Name',
    fieldType: 'string',
  },
  {
    fieldName: 'lastname',
    label: 'Last Name',
    fieldType: 'string',
  },
  {
    fieldName: 'immigrationCurrentStatus',
    label: 'Current Status',
    fieldType: reportingFieldTypes.ENUM,
  },
];

export const defaultCustomTemplateFields = [
  {
    fieldName: 'firstname',
    fieldType: 'string',
    label: 'First Name',
    isActive: true,
    filter: '',
    orderNo: 0,
    filterType: 'contains',
  },
  {
    fieldName: 'middlename',
    fieldType: 'string',
    label: 'Middle Name',
    isActive: true,
    filter: '',
    orderNo: 1,
    filterType: 'contains',
  },
  {
    fieldName: 'lastname',
    fieldType: 'string',
    label: 'Last Name',
    isActive: true,
    filter: '',
    orderNo: 2,
    filterType: 'contains',
  },
  {
    fieldName: 'immigrationCurrentStatus',
    fieldType: 'enum',
    label: 'Current Status',
    isActive: true,
    filter: '',
    orderNo: 3,
    filterType: reportingFieldTypes.ENUM,
  },
];

export interface ColumnType {
  key: string;
  label: string;
  type: string | undefined;
}

export type SelctedFieldType = {
  fieldName: string,
  label: string,
  isActive: boolean,
  filter?: string,
  orderNo: number,
  filterType?: string | null | undefined,
  value?: string | undefined | null | string[],
};

export type Scheduler = {
  name: string;
  eventBridgeschedulerName?: string;
  env?: string;
  endDate: string;
  startDate: string; // will remove optional when back-end is updated
  description: string;
  timeZone: string;
  startTime: string; // will remove optional when back-end is updated
  frequencyNumber: number; // will remove optional when back-end is updated
  frequencyUnit: TimeUnit; // will remove optional when back-end is updated
  entityID?: string | null;
  taskType: string;
  recepientIDs: string[]; // will remove optional when back-end is updated
  companyIDs: string[]; // will remove optional when back-end is updated
  targetURL?: string;
  schedulerID?: string; // Required for delete and update
  taskID?: string; // Required for delete and update
  isNew?: boolean
};

export type CustomTemplateFormType = {
  id?: string | null,
  companyIds: string[],
  teamMemberId?: string | null,
  employerID?: string | null,
  accessibleIds?: Array< string | null > | null,
  name: string,
  selectedFields?: Array<CustomTemplateFields>,
  includeFN?: boolean | null,
  includeCases?: boolean | null,
  individualFN?: boolean | null,
  type: TemplateType,
  isDeleted?: boolean | null,
  viewAccess: AccessPermission | null,
  editAccess: EditPermission | null,
  accessibleCompanyIds: string[] | null,
  category: TemplateCategory | null,
  runReport: boolean;
  execMode: ExecMode;
  schedulers: Scheduler[];
  fromTemplate: boolean;
  parentReportName?: string;
  createdAt?: string | null,
  updatedAt?: string | null,
};

export type SchedulerFormType = {
  schedulers : Array<Scheduler>;
};

export interface ReportType extends ReportEntries {
  [key: string]: number | string | (string | null)[] | null | undefined | boolean | ForeignNational | Company | Report;
}

export const VIEW_OPTIONS = [
  AccessPermission.ONLY_ME,
  AccessPermission.TEAM_AND_SELECTED_COMPANIES,
  AccessPermission.TEAM_AND_ALL_COMPANIES,
];

export const EDIT_OPTIONS = [
  EditPermission.ONLY_MH_ADMINS,
  EditPermission.ONLY_MH_TEAM,
  EditPermission.TEAM_AND_ALL_SELECTED_COMPANIES,
];
export const REPORT_STEPS = ['Select', 'Build', 'Save', 'Automate', 'Run'];

export const EDIT_TEMPLATE_STEPS = ['Edit Build', 'Edit Details', 'Edit Automation'];

export const DATE_FIELD = {
  operator: null, subOperator: null, customNumber: null, startDate: null, endDate: null,
};

export const DEFAULT_FN_FIELDS = ['fullname', 'immigrationCurrentStatus'];

export const DEFAULT_CASE_FIELDS = ['caseNumber', 'requestedProcess', 'fullname', 'companyID', 'sponsoringCompanyFullName'];

export const DEFAULT_COMPANY_FIELDS = ['companyFullName'];

export const DEFAULT_EMPLOYER_FIELDS = ['fullname'];

export const DATE_PICKER_FORMAT = 'MM/dd/yyyy';
export const DATE_TIME_FORMAT = "MM/dd/yyyy 'at' hh:mm a";

export const COMPANY_ERROR = 'Something went wrong while fetching companies.';

export const FULL_SCREEN_ROUTES = ['/create-report'];

export const UPDATE_REPORT_ERROR = 'Something went wrong while updating a report.';
export const GENERAT_REPORT_ERROR = 'Something went wrong while generating a report.';

export const getCompanyOption = (option: Company) => {
  const { name: leganName, data } = option || {};

  return `${leganName || ''} ${data?.doingBusinessAs ? `(dba ${data?.doingBusinessAs})` : ''}`;
};

export const COUNTRY_CODE_FIELDS = ['citizenshipCountry', 'chargeabilityCountry', 'birthCountry'];

export const NO_NAME = '[ No name ]';

export const RequestedProcessVisaStatusValues = [
  VisaStatus.H_4,
  VisaStatus.E_3D,
  VisaStatus.E_3D_CONSULATE,
  VisaStatus.H4_CONSULATE,
  VisaStatus.TD,
  VisaStatus.TD_MEXICO,
  VisaStatus.TD_CANADA,
  VisaStatus.USCIS_DEPENDENT,
  VisaStatus.EAD_DEPENDENT,
  VisaStatus.USCIS_PRIMARY_2,
  VisaStatus.H_1B,
  VisaStatus.PERM_GREEN_CARD,
  VisaStatus.H_1B_LOTTERY,
  VisaStatus.H_1B_24_LOTTERY,
  VisaStatus.H_1B_25_LOTTERY,
  VisaStatus.H_1B_26_LOTTERY,
  VisaStatus.E_3,
  VisaStatus.E_3_CONSULATE,
  VisaStatus.H_1B1,
  VisaStatus.H_1B1_CONSULATE,
  VisaStatus.TN,
  VisaStatus.TN_MEXICO,
  VisaStatus.TN_CANADA,
  VisaStatus.USCIS_PRIMARY,
  VisaStatus.AOS,
  VisaStatus.EAD_PRIMARY,
  VisaStatus.GLOBAL_PROCESS,
];

export const VisaAdditionalProcessingValues = [
  VisaAdditionalProcessing.AP,
  VisaAdditionalProcessing.EAD,
];

export const CompanyContactLabelValues = [
  CompanyContactLabel.TEAM_MEMBER,
  CompanyContactLabel.PRIMARY_CONTACT,
  CompanyContactLabel.INVOICING_CONTACT,
  CompanyContactLabel.SENIOR_CONTACT,
  CompanyContactLabel.SIGNATORY_CONTACT,
];

export const FILTER_AREA_ROW_BREAKPOINT = 1600;

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// without 'is' and 'is not' filters fields.
export const noExactMatchFilterFields = ['fnWorkLocation', 'usResidentialAddress', 'addressPrimary', 'addressSecondary'];

// without 'is not' and 'does not contains' filters fields.
export const noNegativeFilterFields = ['receiptNumber', 'companyWhitelistedDomains'];

export const NO_REPORT_NO_SCHEDULER = 'No Reports';
export const PENDING_AUTOMATION = 'No Reports, Pending Automation';
export const BLOG_POST_URL = 'https://www.meltzerhellrung.com/wp-json/wp/v2/posts';
export const BLOG_POST_PER_PAGE = 10;
export const BLOG_POST_ORDER = 'desc';
export const BLOG_POST_ORDER_BY = 'modified';
export const BLOG_POST_CATEGORY_ID = 27;
